import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useVirtualizer } from "@tanstack/react-virtual"
import { ChevronDown, ChevronRight, X } from "lucide-react"
import { useMemo, useRef, useState, type ReactNode } from "react"
import { useNavigate } from "react-router"
import {
  useArticleSupplierGroups,
  useResolveArticles,
} from "../../api/articlesApi"
import { useResolveSuppliers } from "../../api/suppliersApi"
import { FooterActionBar } from "./FooterActionBar"
import { McPill } from "./McPill"
import { SectionLabel } from "./SectionLabel"

interface RecipientGroup {
  supplierId: string
  supplierName: string
  /** Article-selection flow: the selected article ids under this supplier
   * (each independently removable once the group is expanded). Ids only —
   * article numbers and names are resolved per visible window. */
  articleIds?: string[]
  /** Supplier-selection flow: just the count — scope defaults to the
   * supplier's own articles, nothing to list or remove individually. */
  articlesCount?: number
}

interface SendInquiryPanelProps {
  entityType: "supplier" | "article"
  selectedIds: Set<string>
  /** Removing a recipient (or one of its articles) unchecks the same id(s)
   * back in the sibling list panel — this is the one shared source of truth. */
  onRemoveIds: (ids: string[]) => void
  /** Entity-specific bulk actions, rendered below the recipients and above the
   *  sticky footer. Only the article flow passes one; the inquiry stays the
   *  primary purpose of a selection, so this sits last. */
  moreActions?: ReactNode
}

/** Row height the virtualizer assumes before measuring (px 12.5 text + py-2.5). */
const ARTICLE_ROW_HEIGHT = 37
/** Resolve display fields a block at a time, so scrolling inside one block
 *  hits the cache instead of issuing a request per frame. */
const RESOLVE_BLOCK = 100

/**
 * Replaces the normal detail view when one or more rows are checkbox-selected
 * in the sibling list panel: framework + recipients, scoped down to exactly
 * what's selected, then create the inquiry batch from here.
 *
 * The selection is resolved by the SERVER (one bounded response per flow), not
 * by paging the sibling list until every selected id happens to be loaded —
 * that background paging is what froze the tab on a 41k-article org. Nothing
 * here grows with the number of selected articles: recipients are summarized
 * per supplier, and an expanded group renders a virtualized window.
 */
export function SendInquiryPanel({
  entityType,
  selectedIds,
  onRemoveIds,
  moreActions,
}: SendInquiryPanelProps) {
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Stable identity per selection change: both hooks fingerprint this array
  // for their query key, and `selectedIds` is a fresh Set on every toggle.
  const ids = useMemo(() => [...selectedIds], [selectedIds])

  const supplierQuery = useResolveSuppliers(ids, entityType === "supplier")
  const {
    query: articleQuery,
    isCovered: articlesCovered,
    answersOtherSelection,
  } = useArticleSupplierGroups(ids, entityType === "article")
  const activeQuery = entityType === "supplier" ? supplierQuery : articleQuery
  // The supplier flow submits `selectedIds` itself (see handleSubmit), so a
  // kept previous response can only make its recipient list transiently short,
  // never the inquiry. The article flow derives the recipients FROM the
  // response, so it may only send once that response covers the selection.
  const recipientsCovered = entityType === "supplier" || articlesCovered

  // Both queries keep the previous response while the new selection resolves,
  // so a just-removed id can still be in `data` for one round trip. The live
  // selection therefore has the last word: a removed recipient (or article)
  // disappears on click and the refetch only confirms it.
  const groups = useMemo<RecipientGroup[]>(() => {
    if (entityType === "supplier") {
      return (supplierQuery.data ?? [])
        .filter((supplier) => selectedIds.has(supplier.id))
        .map((supplier) => ({
          supplierId: supplier.id,
          supplierName: supplier.name,
          articlesCount: supplier.articlesCount,
        }))
    }
    return (articleQuery.data ?? [])
      .map((group) => ({
        supplierId: group.supplierId,
        supplierName: group.supplierName,
        articleIds: group.articleIds.filter((id) => selectedIds.has(id)),
      }))
      .filter((group) => group.articleIds.length > 0)
  }, [entityType, supplierQuery.data, articleQuery.data, selectedIds])

  function removeGroup(group: RecipientGroup) {
    // Supplier-selection flow: the checkbox selection *is* the supplier id.
    // Article-selection flow: it's every article id grouped under it.
    onRemoveIds(group.articleIds ?? [group.supplierId])
  }

  function toggleExpanded(supplierId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(supplierId)) next.delete(supplierId)
      else next.add(supplierId)
      return next
    })
  }

  function handleSubmit() {
    const supplierIds =
      entityType === "supplier"
        ? [...selectedIds]
        : groups.map((g) => g.supplierId)
    const params = new URLSearchParams()
    for (const id of supplierIds) params.append("supplierIds", id)
    navigate(
      `/material-compliance/supplier-studio/inquiry/new?${params.toString()}`,
    )
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-2">
          <SectionLabel>{translate("MC_INQ_SEND_WHAT_TO_ASK")}</SectionLabel>
          <div className="rounded-lg border border-yellow1 bg-yellow1/5 px-4 py-3">
            <div className="text-sm font-semibold text-yellow1">
              {translate("MC_INQ_SEND_FRAMEWORK_TITLE")}
            </div>
            <div className="mt-0.5 text-xs text-grey5">
              {translate("MC_INQ_SEND_FRAMEWORK_DESC")}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <SectionLabel>{translate("MC_INQ_SEND_RECIPIENTS")}</SectionLabel>
          <div className="flex flex-col gap-2">
            {(groups.length === 0 || !recipientsCovered) && (
              <p className="text-xs text-grey5">
                {translate(
                  activeQuery.isError || answersOtherSelection
                    ? "MC_INQ_SEND_RESOLVE_ERROR"
                    : activeQuery.isPending || !recipientsCovered
                      ? "MC_INQ_SEND_RESOLVING"
                      : "MC_INQ_SEND_NO_RECIPIENTS",
                )}
              </p>
            )}
            {groups.map((group) => {
              const isExpanded = expanded.has(group.supplierId)
              const listId = `recipients-${group.supplierId}`
              return (
                <div
                  key={group.supplierId}
                  className="rounded-lg border border-grey4 bg-grey2"
                >
                  <div className="flex items-center justify-between gap-2 px-4 py-3">
                    {group.articleIds ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(group.supplierId)}
                        aria-expanded={isExpanded}
                        // Only while the list is mounted: aria-controls must
                        // name an element that is actually in the document.
                        aria-controls={isExpanded ? listId : undefined}
                        className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-white"
                      >
                        {isExpanded ? (
                          <ChevronDown size={14} className="shrink-0" />
                        ) : (
                          <ChevronRight size={14} className="shrink-0" />
                        )}
                        <span className="truncate">{group.supplierName}</span>
                      </button>
                    ) : (
                      <span className="truncate text-sm font-semibold text-white">
                        {group.supplierName}
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <McPill tone="muted">
                        {translate("MC_INQ_SEND_ARTICLES_COUNT", {
                          count:
                            group.articlesCount ??
                            group.articleIds?.length ??
                            0,
                        })}
                      </McPill>
                      <button
                        type="button"
                        onClick={() => removeGroup(group)}
                        className="text-grey5 hover:text-white"
                        aria-label={translate("MC_INQ_SEND_REMOVE", {
                          name: group.supplierName,
                        })}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  {group.articleIds && isExpanded && (
                    <RecipientArticleList
                      id={listId}
                      articleIds={group.articleIds}
                      onRemove={onRemoveIds}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {moreActions}
      </div>

      <FooterActionBar
        left={translate("MC_INQ_SEND_TO_NOTIFY", {
          // The supplier flow submits `selectedIds` itself (see handleSubmit),
          // never the resolved response — so its count must be the live
          // selection, not `groups.length`, which can undercount while a
          // grown selection's resolve is still in flight (kept-previous data
          // only covers the OLD selection). The article flow's count stays
          // `groups.length`: it IS the number of suppliers the resolved
          // response groups the selection under.
          count: entityType === "supplier" ? selectedIds.size : groups.length,
        })}
      >
        <Button
          type="button"
          disabled={groups.length === 0 || !recipientsCovered}
          onClick={handleSubmit}
        >
          {translate("MC_INQ_SEND_BUTTON")}
        </Button>
      </FooterActionBar>
    </div>
  )
}

/**
 * One expanded recipient's articles. Virtualized, and only the ids inside the
 * visible block are resolved into article number + name, so opening a group of
 * 14,828 articles costs one request and a screenful of rows.
 */
function RecipientArticleList({
  id,
  articleIds,
  onRemove,
}: {
  id: string
  articleIds: string[]
  onRemove: (ids: string[]) => void
}) {
  const { translate } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: articleIds.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ARTICLE_ROW_HEIGHT,
    overscan: 8,
  })
  const virtualRows = virtualizer.getVirtualItems()

  // Quantized to whole blocks so the query key (and therefore the request)
  // stays put while scrolling within one, and revisited blocks come from cache.
  const windowIds = useMemo(() => {
    if (virtualRows.length === 0) return []
    const first = virtualRows[0].index
    const last = virtualRows[virtualRows.length - 1].index
    const start = Math.floor(first / RESOLVE_BLOCK) * RESOLVE_BLOCK
    const end = (Math.floor(last / RESOLVE_BLOCK) + 1) * RESOLVE_BLOCK
    return articleIds.slice(start, end)
  }, [virtualRows, articleIds])

  const { data: resolved } = useResolveArticles(windowIds)
  const byId = useMemo(
    () => new Map((resolved ?? []).map((row) => [row.id, row])),
    [resolved],
  )

  return (
    <div
      ref={scrollRef}
      id={id}
      className="max-h-64 overflow-y-auto border-t border-grey4"
    >
      <div
        role="list"
        style={{ height: virtualizer.getTotalSize(), position: "relative" }}
      >
        {virtualRows.map((virtualRow) => {
          const articleId = articleIds[virtualRow.index]
          const row = byId.get(articleId)
          return (
            <div
              key={articleId}
              role="listitem"
              // The list is windowed, so a screen reader cannot count the rows
              // itself — these say how far into the group this row is.
              aria-setsize={articleIds.length}
              aria-posinset={virtualRow.index + 1}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className="flex items-center gap-3 border-t border-grey4 px-4 py-2.5 text-xs first:border-t-0"
            >
              <span className="font-mono text-grey5">
                {row?.articleNumber ?? "…"}
              </span>
              <span className="flex-1 truncate text-white">
                {row?.name ?? ""}
              </span>
              <button
                type="button"
                onClick={() => onRemove([articleId])}
                className="text-grey5 hover:text-white"
                aria-label={translate("MC_INQ_SEND_REMOVE", {
                  name: row?.name ?? articleId,
                })}
              >
                <X size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
